// Use the slim PostHog bundle + slim React entry consistently across the
// app — see `posthogClient.ts` for the rationale. PostHog's docs warn:
// "It's crucial to import posthog-js from the same module path throughout
// your application to avoid multiple bundles." The slim React variant
// (resolved via the `posthog-js/react/slim` subpath) injects the client
// via the `<PostHogProvider client={...}>` prop instead of importing the
// SDK itself, so this pairing keeps a single SDK copy in the bundle.
import posthog from 'posthog-js/dist/module.slim';
import { PostHogProvider } from 'posthog-js/react/slim';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { TanstackRouterProvider } from './components/_core/providers/TanstackRouterProvider';
import './config/sentry';
import './i18n/config';
import { TitleCacheProvider } from './providers/TitleCacheProvider';
import { initializeThemeEarly } from './stores/themeStore';
import { startBootTokenWarmup } from './utils/auth/boot-token-warmup';
import { initializePostHog, startFeatureFlagPolling } from './utils/feature-flags/posthogClient';
import { loadDeferredRoutes } from './utils/routing/deferred-route-loader';
import { isDeferredRoutePath } from './utils/routing/deferred-routes';

// Initialize theme before React renders to prevent flash of wrong theme
initializeThemeEarly();

//@ts-expect-error BigInt.prototype.toJSON is not defined in TypeScript
BigInt.prototype.toJSON = function () {
    const int = Number.parseInt(this.toString(), 10);
    return int ?? this.toString();
};

// Kick off PostHog initialisation BEFORE React render. The synchronous portion
// of `initializePostHog()` (config read + `posthog.init()` with server-injected
// bootstrap data) runs before this expression returns, so `posthog` has its
// bootstrap feature flags loaded by the time the React tree mounts. The async
// tail (`waitForFeatureFlags` for non-bootstrap users, identification, etc.)
// is allowed to complete in the background — we no longer block first paint
// on it. See `client/src/utils/feature-flags/posthogClient.ts`.
//
// Defence-in-depth: `getFlag`/`getFlagSync` also fall back to reading directly
// from `window.__POSTHOG_BOOTSTRAP__` if called before init completes.
void initializePostHog().then((initialized) => {
    if (initialized) {
        startFeatureFlagPolling(30000);
    }
});

// Start the Auth0 silent refresh a console or attestation-link hard load needs BEFORE React
// renders. Left to `RequireAuth`, the exchange cannot begin until its lazily loaded chunk has
// downloaded and the tree has mounted — Sentry puts that at 1.2-1.5 s into a cold load, with
// the network idle for most of it and the whole page held behind a spinner until it returns
// (console p75 LCP 2378 ms vs FCP 888 ms). Everything needed to decide whether a refresh is
// due is already in `localStorage` when this module runs, so the round trip overlaps JS boot
// instead of following it. `RequireAuth` adopts the in-flight promise; the warm-up itself
// touches no store state, so what is awaited and trusted is unchanged. See CUR-1459 and
// CUR-1580.
startBootTokenWarmup();

/**
 * Marks the app as mounted on React's first commit. The inline bootstrap script in
 * `index.html` watches `window.__CURIUM_APP_MOUNTED__`: if it is never set (e.g. a
 * boot-critical chunk failed to load and this module's graph never executed), the boot
 * watchdog there recovers instead of leaving the user stuck on the splash. See
 * `client/index.html`.
 */
function MountBeacon() {
    useEffect(() => {
        window.__CURIUM_APP_MOUNTED__ = true;
    }, []);
    return null;
}

const container = document.getElementById('root');
const root = createRoot(container!);

function mount() {
    root.render(
        // The slim React entry's `PostHogProvider` types its `client` against
        // `posthog-js` (default), even though it accepts any PostHog instance
        // at runtime. Cast through `never` to bridge the slim / default
        // PostHog identity mismatch documented in `posthogClient.ts`.
        <PostHogProvider client={posthog as unknown as never}>
            <TitleCacheProvider>
                <MountBeacon />
                <TanstackRouterProvider />
            </TitleCacheProvider>
        </PostHogProvider>,
    );
}

// A hard load inside a deferred route area (the admin console) needs that area's routes before
// the router first matches the URL, so they load while the boot splash is still up. Every other
// load mounts at once, as before. A failed load is left as an unhandled rejection on purpose: the
// chunk error handler in `index.html` treats it like any other boot chunk that failed to load.
if (isDeferredRoutePath(window.location.pathname)) {
    void loadDeferredRoutes().then(mount);
} else {
    mount();
}
